import {
  buildGraph,
  type ConnectionInput,
  type Graph as Graphv2,
  type StationInput,
  type TransitionInput,
  type VertexType,
} from '@metro/graph'
import {
  OnlineRoutingError,
  RoutingError,
  type Leg,
  type OnlineRoutingClient,
  type OnlineRoutingOptions,
  type Route,
  type Stop,
  recomputeID,
  signatureKey,
} from '@metro/routing'
import type { LineDisplay } from '@metro/route-card'
import type { LineType, SchemaData, SchemaStation } from '../schema/types'
import { lineHexForTheme } from '../schema/lineColor'
import { buildOnlineRouteSchemaGuard } from './onlineRouteSchemaGuard'
import { buildStationComplexIndex, type ComplexStation } from './stationComplexIndex'
import { buildStationComplexStops } from './stationComplexStops'
import { createOfflineRouteSearch, type OfflineRouteInputs } from './offlineRouteSearch'
import { inThreadOfflineSearchRunner, type OfflineSearchRunner } from './offlineSearchRunner'
import type { Theme } from './metroMapStore'
import {
  noopRoutingTelemetryReporter,
  type RoutingAggregateSource,
  type RoutingErrorKind,
  type RoutingRouteSource,
  type RoutingTelemetryReporter,
} from './routingTelemetryReporter'

/**
 * Channel-keyed lifetime warn-once. Mirrors the iOS
 * `RoutingLogger.warnOnce(channel:_:)` shape (commit `e6ff9f2d`) and the
 * Android `RoutingLogger.warnOnce` Kotlin twin shipped under TASK-274 W1.
 *
 * Cross-platform contract: the channel string ids are part of the contract
 * — server-side log aggregation (when wired) groups by these channel keys
 * across all three clients. The known channels are:
 *   - `adapter.connection.skipped`
 *   - `adapter.transition.skipped`
 *   - `adapter.lineType.fallback.<lineType>`
 *   - `adapter.online.fallback`
 *
 * Why lifetime (not per-`buildAdapter`-call): today web's `buildAdapter`
 * is memoized by the controller so the per-call counter approach we used
 * to ship was effectively once-per-schema-load. A future refactor that
 * breaks memoization (e.g. re-building the adapter per render) would
 * silently turn the dev-warn into per-frame spam — the iOS / Android
 * warn-once shape seals that footgun.
 *
 * Each channel keeps a `Set<string>` of seen ids so independent ids on
 * the same channel emit independently (e.g. two distinct fallback line
 * types still each warn once). Pass an `id` matching the cause — typical
 * choices are the skipped-record id, or the LineType for the fallback
 * channel.
 */
const _warnOnceByChannel: Map<string, Set<string>> = new Map()

/**
 * Emit `console.warn(message)` at most once per `(channel, id)` pair for
 * the lifetime of the page session. Subsequent calls with the same
 * channel + id are silently dropped. Different ids on the same channel
 * — and different channels entirely — emit independently.
 *
 * No-op in production builds (matches iOS DEBUG-only default and the
 * pre-existing `process.env.NODE_ENV !== 'production'` gate this
 * function replaces).
 */
function _warnOnce(channel: string, id: string, message: string): void {
  if (process.env.NODE_ENV === 'production') return
  let seen = _warnOnceByChannel.get(channel)
  if (seen === undefined) {
    seen = new Set()
    _warnOnceByChannel.set(channel, seen)
  }
  if (seen.has(id)) return
  seen.add(id)
  console.warn(message)
}

/**
 * Test-only seam — reset the warn-once channel map so a unit test can
 * re-trigger the first-emission branch deterministically. Mirrors the
 * iOS `RoutingLogger._resetWarnOnceChannelsForTesting()` shape.
 */
export function __resetWarnOnceForTesting(): void {
  _warnOnceByChannel.clear()
}

/**
 * Lifecycle adapter that stitches the schema (`SchemaData`) to the
 * Routing algorithm (`@metro/routing`) via the graph builder
 * (`@metro/graph`).
 *
 * Owned state:
 *   - The constructed `Graph` (built once per `buildAdapter` call).
 *   - The `lineLookup` (line_id → display info) consumed by RouteCard /
 *     RouteResultsList.
 *   - NaPTAN-keyed lookup maps for online ↔ offline ID resolution.
 *   - An LRU cache of recent `[Route]` arrays (capacity 50, mirrors iOS
 *     `RoutingAdapter._cache`).
 *   - A serialization slot for `findRoutes` calls — only one in-flight
 *     algorithm run at a time, mirroring the iOS/Android Phase 1.5
 *     contract that the Graph mutates `deactivated` during Yen-K and
 *     therefore is not safe to share across overlapping awaits.
 */
export interface RoutingAdapter {
  /**
   * Constructed graph. Exposed for diagnostics; do NOT mutate from
   * outside.
   */
  readonly graph: Graphv2
  /**
   * `line_id` → display info (localised name + hex color). Caller passes
   * straight into `RouteCard` / `RouteResultsList`.
   */
  /**
   * Theme-aware `line_id → display info` lookup (2026-07 color
   * contract). The hex is `colorModes[theme] ?? hexColor` — the same
   * palette the per-theme MMTG map geometry is baked from, so pills /
   * badges / rails always match the map. Callers pass the SAME theme
   * signal that drives the map (`metroMapStore.state.theme`).
   */
  lineLookupFor(theme: Theme): ReadonlyMap<string, LineDisplay>
  /**
   * Serializes findRoutes calls (single in-flight Promise). Awaits any
   * prior call before starting the new one.
   *
   * Pipeline (matches `metro-ios/.../RoutingAdapter.swift:285–476`):
   *   1. LRU cache lookup (capacity 50, key = `from|to|departAt|arriveAt|pref`).
   *   2. Online attempt (`OnlineRoutingClient.fetchRoutes`) when wired
   *      AND both endpoints resolve through the NaPTAN map.
   *   3. NaPTAN→schema-id upgrade on online response.
   *   4. Pure-transit guarantee: if online has at least one ride-and-no-
   *      walk route, return online cached. Otherwise stash + fall through.
   *   5. Offline search (`OfflineRouteSearch`: standard, pure-transit and
   *      fewest-changes passes, run by the `OfflineSearchRunner`) —
   *      merged with the stash; class-separated Pareto collapse picks
   *      the best of each transport mode.
   *
   * @throws {RoutingError} `'NoPath'` / `'SameOriginDestination'`.
   */
  findRoutes(
    fromStationId: string,
    toStationId: string,
    options?: OnlineRoutingOptions,
    signal?: AbortSignal,
  ): Promise<Route[]>
  /**
   * Per-route source for the route at `index` in the most recent
   * successful `findRoutes` result. `undefined` when `index` is out
   * of range — the controller skips emitting `route_variant_tapped`
   * in that case (the tap references a stale variant list).
   */
  routeSource(index: number): RoutingRouteSource | undefined
  /**
   * Releases the offline search runner — terminates its Web Worker when
   * the page runs the search in one. The Graph itself is plain JS and gets
   * GC'd when the adapter is dropped.
   */
  dispose(): void
}

/** Map a web `LineType` to the Graph `VertexType` enum. */
function mapVertexType(lineType: LineType): VertexType {
  switch (lineType) {
    case 'UNDERGROUND':
      return 'tube'
    case 'DLR':
      return 'dlr'
    case 'OVERGROUND':
      return 'overground'
    case 'ELIZABETH_LINE':
      return 'elizabeth'
    case 'TRAM':
      return 'tram'
    case 'CABLE_CAR':
    case 'NATIONAL_RAIL':
      // Graph doesn't carry these as first-class VertexType values
      // today. Falling back to 'tube' keeps routing usable; the
      // dev-warn surfaces the gap so we can revisit when the algorithm
      // grows new line categories.
      //
      // Channel `adapter.lineType.fallback.<type>` matches the iOS /
      // Android string ids exactly (see RoutingLogger on both
      // platforms) so server-side log aggregation can group across
      // clients.
      _warnOnce(
        `adapter.lineType.fallback.${lineType}`,
        lineType,
        `[routingAdapter] LineType ${lineType} has no Graph VertexType — defaulting to 'tube'.`,
      )
      return 'tube'
  }
}

/**
 * Minimal LRU cache (capacity 50 default). Mirrors iOS
 * `RoutingLRUCache` — `get` moves the key to the most-recent slot,
 * `set` evicts the head when the cache is full.
 */
class _LRU<K, V> {
  private readonly _capacity: number
  private readonly _map: Map<K, V>
  constructor(capacity: number) {
    this._capacity = capacity
    this._map = new Map()
  }
  get(key: K): V | undefined {
    const v = this._map.get(key)
    if (v === undefined) return undefined
    // Map preserves insertion order — re-insert to move to tail.
    this._map.delete(key)
    this._map.set(key, v)
    return v
  }
  set(key: K, value: V): void {
    if (this._map.has(key)) this._map.delete(key)
    this._map.set(key, value)
    if (this._map.size > this._capacity) {
      const oldest = this._map.keys().next().value as K
      this._map.delete(oldest)
    }
  }
}

const _CACHE_CAPACITY = 50

/**
 * Transport-mode class used for Pareto / dominance grouping.
 * 0 = walking-only, 1 = transit-with-walking, 2 = pure-transit
 * (no walk legs). Lower numbers are NOT "worse" — these classes
 * are independent dominance lanes, not a quality ordering.
 */
function _routeClass(r: Route): number {
  const hasRide = r.legs.some((l) => l.type === 'ride')
  if (!hasRide) return 0
  const hasWalk = r.legs.some((l) => l.type === 'walk')
  return hasWalk ? 1 : 2
}

/** Cap on distinct route variants surfaced per From/To pair. Matches the
 *  server `MapJourneysWithAnchors` + iOS/Android `collapseVariants` cap. */
const _MAX_VARIANTS = 3

/**
 * Reduce a route list to a boarding+line-sequence-unique set, capped to the
 * three fastest. Mirrors server `MapJourneysWithAnchors` and iOS/Android
 * `collapseVariants` byte-for-byte in semantics.
 *
 * Deliberately does NOT run a pure (time,xfer) Pareto filter: that
 * over-collapsed genuinely different journeys — e.g. Aldgate→Euston's
 * met→nor vs nor→met vs met→cir→nor all boarding at the same station were
 * knocked out by the single fastest, leaving one route. Layer-A pruning in
 * `packages/routing` `findRoutes` (dedupBySignature / direct-ride-wins /
 * transfer-count dominance / maxVariants) already removes true duplicates
 * upstream; this stage only deduplicates and caps.
 *
 * Step A — dedup by `(modeClass, boardingId, lineSeq)`, keeping the fastest
 * (min `total_time_s`) per key.
 *   - modeClass: `_routeClass` (0 walk-only / 1 transit+walk / 2 pure-transit),
 *     so an accessible walking variant never collapses into a transit one.
 *   - boardingId: first ride leg's first stop, naptan-preferred. Routes that
 *     leave from different stations are distinct even on the same lines.
 *   - lineSeq: ordered ride-leg `line_id`s.
 *   Walk-only routes (no ride legs) key on `(0, "", "")` — no bypass — so
 *   multiple walking variants collapse to the single fastest, matching server
 *   `MapJourneysWithAnchors` and iOS/Android `collapseVariants` byte-for-byte.
 *
 * Step B — total-order sort: ascending by `total_time_s`, tie-broken by the
 * canonical dedup signature (`signatureKey`) ascending. The total order makes
 * the cap boundary deterministic across platforms.
 *
 * Step C — cap to the three fastest survivors overall.
 *
 * Step D — pure-transit (step-free) guarantee: if a `_routeClass === 2` route
 * exists among the survivors but none reached the top-3, evict the slowest
 * capped entry and insert the fastest pure-transit, then re-sort. Keeps a
 * step-free option visible when the fastest journeys are all walk-hybrids.
 */
function _collapseVariants(routes: readonly Route[]): Route[] {
  if (routes.length <= 1) return routes.slice()
  const bestByKey = new Map<string, Route>()
  for (const r of routes) {
    const rides = r.legs.filter((l) => l.type === 'ride')
    // Walk-only routes (no ride legs) fall through with boardingId "" and
    // lineSeq "" -> key collapses to the single fastest. No bypass, matching
    // server MapJourneysWithAnchors + iOS/Android collapseVariants byte-for-byte.
    const boarding = rides[0]?.stops?.[0]
    const boardingId = boarding?.naptanId ?? boarding?.stationId ?? ''
    const lineSeq = rides.map((l) => l.line_id ?? '').join('|')
    const key = `${_routeClass(r)} ${boardingId} ${lineSeq}`
    const existing = bestByKey.get(key)
    if (existing === undefined || r.total_time_s < existing.total_time_s) {
      bestByKey.set(key, r)
    }
  }
  // Total-order comparator: ascending by total_time_s, tie-broken by the
  // canonical dedup signature string ascending. A total order makes the
  // cap boundary deterministic across platforms (server/android/ios/web).
  const byTimeThenSignature = (a: Route, b: Route): number => {
    if (a.total_time_s !== b.total_time_s) return a.total_time_s - b.total_time_s
    const sa = signatureKey(a)
    const sb = signatureKey(b)
    return sa < sb ? -1 : sa > sb ? 1 : 0
  }
  const survivors = [...bestByKey.values()]
  survivors.sort(byTimeThenSignature)
  const result = survivors.slice(0, _MAX_VARIANTS)
  // Pure-transit (step-free) guarantee slot: if any pure-transit route
  // (_routeClass === 2, no walk legs) exists among the survivors but none
  // reached the capped top-3, evict the slowest current entry and insert the
  // fastest pure-transit, then re-sort. Guarantees a step-free option stays
  // visible when the fastest journeys are all walk-hybrids. No change when the
  // result already has a pure-transit route or holds fewer than the cap.
  const pureTransit = survivors.filter((r) => _routeClass(r) === 2)
  if (
    pureTransit.length > 0 &&
    result.length === _MAX_VARIANTS &&
    !result.some((r) => _routeClass(r) === 2)
  ) {
    result[result.length - 1] = pureTransit[0]
    result.sort(byTimeThenSignature)
  }
  return result
}

/** Test-only seam for the class-separated Pareto helper. */
export const __internal_collapseVariants = _collapseVariants

export { FEWEST_CHANGES_PENALTY_SEC } from './offlineRouteSearch'

/**
 * Build a `RoutingAdapter` from the parsed schema and an optional online
 * client. Pass `onlineClient: undefined` to force offline-only behaviour
 * (matches the previous behaviour before TASK-273).
 */
export function buildAdapter(
  schema: SchemaData,
  opts: {
    readonly onlineClient?: OnlineRoutingClient
    readonly cacheCapacity?: number
    readonly telemetry?: RoutingTelemetryReporter
    /**
     * Where the offline search runs, built from the graph inputs. Default:
     * on the calling thread. The page passes `createWorkerOfflineSearchRunner`
     * so a search never freezes it.
     */
    readonly offlineSearchRunner?: (inputs: OfflineRouteInputs) => OfflineSearchRunner
    /**
     * The stops the station complexes are read from. Default: the schema's.
     * The night map drops the stops of lines that do not run, so the page
     * passes the unfiltered stops — a picked Bakerloo stop at Oxford Circus
     * still knows its Central and Victoria siblings (`stationComplexStops`).
     */
    readonly complexStations?: readonly ComplexStation[]
  } = {},
): RoutingAdapter {
  const telemetry = opts.telemetry ?? noopRoutingTelemetryReporter
  // Parallel array — `lastRouteSources[i]` is the per-route source for
  // the route at index `i` in the most recent successful `findRoutes`
  // result. Read by the controller when emitting `route_variant_tapped`.
  let lastRouteSources: RoutingRouteSource[] = []
  // --- stations ---
  const stationInputs: StationInput[] = []
  const stationsById = new Map<string, SchemaStation>()
  for (const s of schema.stations) {
    stationsById.set(s.id, s)
    stationInputs.push({
      id: s.id,
      x: s.x,
      y: s.y,
      type: mapVertexType(s.line.type),
      lines: [s.line.id],
      naptanId: s.naptanId,
    })
  }

  // --- connections (skip + warn-once on missing routing fields) ---
  const connectionInputs: ConnectionInput[] = []
  const skippedConnections: string[] = []
  for (const c of schema.connections) {
    if (c.weight === undefined || c.fromStationId === undefined || c.toStationId === undefined) {
      skippedConnections.push(c.id)
      continue
    }
    connectionInputs.push({
      id: c.id,
      lineID: c.lineId,
      fromID: c.fromStationId,
      toID: c.toStationId,
      weightSec: c.weight,
      reversible: c.reversible ?? false,
    })
  }
  for (const id of skippedConnections) {
    _warnOnce(
      'adapter.connection.skipped',
      id,
      `[routingAdapter] Skipped connection ${id}: missing weight/fromStationId/toStationId.`,
    )
  }

  // --- transitions ---
  const transitionInputs: TransitionInput[] = []
  const skippedTransitions: string[] = []
  for (const t of schema.transitions ?? []) {
    if (t.weight === undefined) {
      skippedTransitions.push(t.id)
      continue
    }
    const fromLine = stationsById.get(t.fromStationId)?.line.id
    const toLine = stationsById.get(t.toStationId)?.line.id
    if (fromLine === undefined || toLine === undefined) {
      skippedTransitions.push(t.id)
      continue
    }
    transitionInputs.push({
      id: t.id,
      fromID: t.fromStationId,
      toID: t.toStationId,
      fromLine,
      toLine,
      weightSec: t.weight,
      reversible: t.reversible ?? false,
      street: t.street,
    })
  }
  for (const id of skippedTransitions) {
    _warnOnce(
      'adapter.transition.skipped',
      id,
      `[routingAdapter] Skipped transition ${id}: missing weight or unresolved line lookup.`,
    )
  }

  const graph = buildGraph({
    stations: stationInputs,
    connections: connectionInputs,
    transitions: transitionInputs,
  })

  // --- line lookup (one map per theme; 2026-07 color contract) ---
  const lineLookups: Record<Theme, Map<string, LineDisplay>> = {
    light: new Map(),
    dark: new Map(),
  }
  for (const s of schema.stations) {
    for (const theme of ['light', 'dark'] as const) {
      const lookup = lineLookups[theme]
      if (lookup.has(s.line.id)) continue
      const shortName = s.line.shortLocalName?.trim()
      lookup.set(s.line.id, {
        localName: s.line.localName ?? s.line.id,
        hexColor: lineHexForTheme(s.line, theme),
        ...(shortName ? { shortName } : {}),
      })
    }
  }

  // --- NaPTAN lookup maps ---
  // `naptanLookup[stationId] = naptanId` — populated only for stations
  // carrying a non-empty NaPTAN (tram stops without NaPTAN can't take the
  // online path and must stay offline-only).
  const naptanLookup = new Map<string, string>()
  // `stationByNaptanLine[naptanId][lineId] = stationId` — per-line context
  // upgrade. Some hub NaPTANs map to multiple per-line records; the line
  // context disambiguates.
  const stationByNaptanLine = new Map<string, Map<string, string>>()
  // `stationsByNaptan[naptanId] = stationId` — any-line fallback for
  // transfer / walk legs whose line context isn't authoritative.
  const stationsByNaptan = new Map<string, string>()
  for (const s of schema.stations) {
    if (s.naptanId === undefined || s.naptanId.length === 0) continue
    naptanLookup.set(s.id, s.naptanId)
    let perLine = stationByNaptanLine.get(s.naptanId)
    if (perLine === undefined) {
      perLine = new Map()
      stationByNaptanLine.set(s.naptanId, perLine)
    }
    if (!perLine.has(s.line.id)) {
      perLine.set(s.line.id, s.id)
    }
    if (!stationsByNaptan.has(s.naptanId)) {
      stationsByNaptan.set(s.naptanId, s.id)
    }
  }

  const onlineClient = opts.onlineClient
  // Online routes naming ids this schema lacks are dropped (an emptied set
  // falls through to offline) — see `buildOnlineRouteSchemaGuard`.
  const admitsOnline = buildOnlineRouteSchemaGuard(schema)
  const schemaStationIds = new Set(schema.stations.map((s) => s.id))
  // Destination complexes for the trailing-transfer truncation
  // (`RouteOptions.dstNaptanIds`).
  const complexStations = (opts.complexStations ?? schema.stations).map(
    ({ id, slug, naptanId }) => ({
      id,
      slug,
      naptanId,
    }),
  )
  const complexNaptansOf = buildStationComplexIndex({ stations: complexStations })
  const cache = new _LRU<string, Route[]>(opts.cacheCapacity ?? _CACHE_CAPACITY)
  // Negative-result cache: short-TTL throttling for keys that resulted
  // in `NoPath` from BOTH online (empty / failure) AND offline. Without
  // this, the user repeatedly hammering a no-route pair (Morden South →
  // Mill Hill East off-hours, etc.) re-runs the 3-second online timeout
  // + offline Yen-K on every retry. TTL is short (30 s) so legitimate
  // schedule changes still get a fresh probe.
  const negativeCache = new Map<string, number>()
  const NEGATIVE_TTL_MS = 30_000

  // --- serialized OFFLINE-only gate ---
  // Mirror iOS+Android model: only the offline arm needs the Graph
  // mutation lock (Yen-K toggles `deactivated` flags on the shared
  // graph). Online calls are independent network requests and can run
  // in parallel — wrapping them in this gate would needlessly stall a
  // second user-initiated request behind the first one's 3 s timeout
  // (G-14 in TASK-273 integration audit).
  let offlineInFlight: Promise<Route[]> | null = null

  /**
   * Cache key shape mirrors iOS `_cacheKey` (`:611–620`). Empty option
   * fields collapse to empty string. `|` cannot appear in station ids
   * or NaPTANs by schema constraint.
   */
  const _cacheKey = (fromID: string, toID: string, options?: OnlineRoutingOptions): string =>
    `${fromID}|${toID}|${options?.departAt ?? ''}|${options?.arriveAt ?? ''}|${options?.accessibilityPreference ?? ''}`

  const emitSucceeded = (
    fromID: string,
    toID: string,
    routes: readonly Route[],
    perRouteSource: readonly RoutingRouteSource[],
    aggregate: RoutingAggregateSource,
    startedAt: number,
    cacheHit: boolean,
    onlineUsedFallback: boolean,
  ): void => {
    let walkingOnly = 0
    let hybrid = 0
    let pureTransit = 0
    for (const r of routes) {
      const hasRide = r.legs.some((l) => l.type === 'ride')
      if (!hasRide) {
        walkingOnly += 1
        continue
      }
      const hasWalk = r.legs.some((l) => l.type === 'walk')
      if (hasWalk) hybrid += 1
      else pureTransit += 1
    }
    const online = perRouteSource.filter((s) => s === 'tfl').length
    const offline = perRouteSource.filter((s) => s === 'offline').length
    telemetry({
      kind: 'resolveSucceeded',
      fromStation: fromID,
      toStation: toID,
      source: aggregate,
      routeCount: routes.length,
      onlineCount: online,
      offlineCount: offline,
      walkingOnlyCount: walkingOnly,
      hybridCount: hybrid,
      pureTransitCount: pureTransit,
      durationMs: Date.now() - startedAt,
      cacheHit,
      onlineUsedFallback,
    })
  }

  const emitFailed = (
    fromID: string,
    toID: string,
    errorKind: RoutingErrorKind,
    startedAt: number,
  ): void => {
    telemetry({
      kind: 'resolveFailed',
      fromStation: fromID,
      toStation: toID,
      errorKind,
      durationMs: Date.now() - startedAt,
    })
  }

  const mapErrorKind = (err: unknown): RoutingErrorKind => {
    if (err instanceof RoutingError) {
      return err.code === 'NoPath' ? 'no_path' : 'same_origin'
    }
    // OnlineRoutingError carries a `detail` discriminant — map to the
    // cross-platform taxonomy. HTTP `upstream` splits on status range
    // so the dashboard can separate 4xx (client) from 5xx (server).
    if (err instanceof OnlineRoutingError) {
      const d = err.detail
      switch (d.kind) {
        case 'timeout':
          return 'timeout'
        case 'transport':
          return 'network'
        case 'decode':
          return 'decode'
        case 'upstream': {
          const s = d.status
          if (s >= 400 && s < 500) return 'http_4xx'
          if (s >= 500 && s < 600) return 'http_5xx'
          return 'other'
        }
      }
    }
    if (err instanceof Error) {
      const name = err.name
      if (name === 'AbortError') return 'timeout'
      if (name === 'TimeoutError') return 'timeout'
      if (name === 'TypeError') return 'network'
      if (name === 'SyntaxError') return 'decode'
    }
    return 'other'
  }

  /**
   * Walk every leg + stop, replace any id the schema does not carry with the
   * resolved schema id. Ride legs prefer the per-line context; transfer /
   * walk legs fall straight to the any-line map. Mirrors iOS
   * `_upgradeNaptanIDs` (`:559–605`).
   */
  const _upgradeID = (id: string, naptan: string | undefined, lineID: string): string | null => {
    if (schemaStationIds.has(id)) return id
    const key = naptan ?? id
    if (lineID.length > 0) {
      const perLine = stationByNaptanLine.get(key)
      const byLine = perLine?.get(lineID)
      if (byLine !== undefined) return byLine
    }
    const anyLine = stationsByNaptan.get(key)
    return anyLine ?? null
  }

  const _upgradeNaptanIDs = (route: Route): Route => {
    let mutated = false
    const newLegs: Leg[] = route.legs.map((leg) => {
      const lineID = leg.line_id ?? ''
      const stops = leg.stops ?? []
      const newStops: Stop[] = stops.map((stop) => {
        const upgraded = _upgradeID(stop.stationId, stop.naptanId, lineID)
        if (upgraded === null || upgraded === stop.stationId) return stop
        mutated = true
        return { ...stop, stationId: upgraded }
      })
      const newFrom =
        _upgradeID(leg.from_station_id ?? '', undefined, lineID) ?? leg.from_station_id
      const newTo = _upgradeID(leg.to_station_id ?? '', undefined, lineID) ?? leg.to_station_id
      if (newStops === stops && newFrom === leg.from_station_id && newTo === leg.to_station_id) {
        return leg
      }
      mutated = true
      return {
        ...leg,
        stops: newStops,
        from_station_id: newFrom ?? '',
        to_station_id: newTo ?? '',
      }
    })
    if (!mutated) return route
    return recomputeID({ ...route, legs: newLegs })
  }

  const offlineRunner =
    opts.offlineSearchRunner?.({
      stations: stationInputs,
      connections: connectionInputs,
      transitions: transitionInputs,
      complexStations,
    }) ??
    inThreadOfflineSearchRunner(
      createOfflineRouteSearch(
        graph,
        complexNaptansOf,
        buildStationComplexStops({ stations: complexStations }),
      ),
    )

  const findRoutesOrchestrated = async (
    fromID: string,
    toID: string,
    options?: OnlineRoutingOptions,
    signal?: AbortSignal,
  ): Promise<Route[]> => {
    const startedAt = Date.now()
    telemetry({
      kind: 'resolveStarted',
      fromStation: fromID,
      toStation: toID,
      accessibilityPref: options?.accessibilityPreference,
      withDepartAt: (options?.departAt ?? '').length > 0,
      withArriveAt: (options?.arriveAt ?? '').length > 0,
    })
    // Cache lookup runs BEFORE any serialization gate — a cached hit
    // must short-circuit even when a prior offline run is mid-Yen-K.
    // (G-13 in TASK-273 integration audit: previous shape paid for
    // the in-flight wait even on cache hits.)
    const cacheKey = _cacheKey(fromID, toID, options)
    const cached = cache.get(cacheKey)
    if (cached !== undefined) {
      lastRouteSources = new Array<RoutingRouteSource>(cached.length).fill('cache')
      emitSucceeded(fromID, toID, cached, lastRouteSources, 'cache', startedAt, true, false)
      return cached
    }

    // Negative-result cache: same expensive (timeout + offline NoPath)
    // path was traversed for this key within TTL — short-circuit with
    // the same RoutingError the caller would have eventually received.
    const negativeAt = negativeCache.get(cacheKey)
    if (negativeAt !== undefined) {
      if (Date.now() - negativeAt < NEGATIVE_TTL_MS) {
        emitFailed(fromID, toID, 'no_path', startedAt)
        throw new RoutingError('NoPath')
      }
      negativeCache.delete(cacheKey)
    }

    // Online path runs OUTSIDE the offline gate — independent network
    // requests don't need Graph serialization. Subtle behavior note
    // (G-28): when online happens to include at least one pure-transit
    // route, we skip offline entirely and cache the online-only result.
    // When online is walking-only / hybrid only, we fall through to
    // offline merge. So identical From/To searched repeatedly can
    // return different route sets if the backend toggles between
    // "with pure-transit" and "without" modes between calls — by
    // design, since paying for offline Yen-K when online already
    // covered the pure-transit case is wasteful.
    let supplementalOnlineRoutes: Route[] = []
    if (onlineClient !== undefined) {
      const fromNaptan = naptanLookup.get(fromID)
      const toNaptan = naptanLookup.get(toID)
      if (fromNaptan !== undefined && toNaptan !== undefined) {
        try {
          const routes = await onlineClient.fetchRoutes(fromNaptan, toNaptan, options, signal)
          if (routes.length > 0) {
            const upgraded = routes.map(_upgradeNaptanIDs).filter(admitsOnline)
            const onlineHasPureTransit = upgraded.some(
              (r) =>
                r.legs.some((l) => l.type === 'ride') && !r.legs.some((l) => l.type === 'walk'),
            )
            if (onlineHasPureTransit) {
              const collapsed = _collapseVariants(upgraded)
              cache.set(cacheKey, collapsed)
              const sources = new Array<RoutingRouteSource>(collapsed.length).fill('tfl')
              lastRouteSources = sources
              emitSucceeded(fromID, toID, collapsed, sources, 'tfl', startedAt, false, false)
              return collapsed
            }
            supplementalOnlineRoutes = upgraded
          }
        } catch {
          _warnOnce(
            'adapter.online.fallback',
            `${fromNaptan}->${toNaptan}`,
            `[routingAdapter] Online route fetch failed (${fromNaptan} → ${toNaptan}) — falling back to offline graph.`,
          )
        }
      }
    }

    // Offline arm — serialised via `offlineInFlight` because Yen-K
    // mutates the shared Graph's `deactivated` flags during search.
    // Wait for any prior offline run to settle before kicking ours.
    if (offlineInFlight) {
      await offlineInFlight.catch(() => {})
    }
    const offlineWork = (async (): Promise<Route[]> => {
      try {
        const offline = await offlineRunner.run(fromID, toID)
        const merged = supplementalOnlineRoutes.concat(offline)
        const collapsed = _collapseVariants(merged)
        cache.set(cacheKey, collapsed)
        // Attribute survivors by id-membership against the pre-collapse
        // online subset. Collapse selects a subset of the input list
        // without mutating routes, so `route.id` membership is stable
        // across the boundary.
        const onlineIds = new Set<string>()
        for (const r of supplementalOnlineRoutes) onlineIds.add(r.id)
        const sources: RoutingRouteSource[] = collapsed.map((r) =>
          onlineIds.has(r.id) ? 'tfl' : 'offline',
        )
        lastRouteSources = sources
        const hasOnline = sources.includes('tfl')
        const hasOffline = sources.includes('offline')
        const aggregate: RoutingAggregateSource =
          hasOnline && hasOffline ? 'mixed' : hasOnline ? 'tfl' : 'offline'
        emitSucceeded(fromID, toID, collapsed, sources, aggregate, startedAt, false, !hasOnline)
        return collapsed
      } catch (err) {
        // G-20: when offline throws but online produced walking-only
        // supplements, surface those instead of propagating the offline
        // error. This is the scenario the supplemental-online-routes
        // mechanism was designed for — TFL "walking only" + offline
        // graph "no path" should still show the walking option.
        if (err instanceof RoutingError && supplementalOnlineRoutes.length > 0) {
          const collapsed = _collapseVariants(supplementalOnlineRoutes)
          cache.set(cacheKey, collapsed)
          const sources = new Array<RoutingRouteSource>(collapsed.length).fill('tfl')
          lastRouteSources = sources
          emitSucceeded(fromID, toID, collapsed, sources, 'tfl', startedAt, false, false)
          return collapsed
        }
        if (err instanceof RoutingError) {
          // G-22/G-23: record the no-result for short-TTL throttling so
          // the user spamming search doesn't re-fire the full pipeline.
          negativeCache.set(cacheKey, Date.now())
        }
        emitFailed(fromID, toID, mapErrorKind(err), startedAt)
        throw err
      }
    })()
    offlineInFlight = offlineWork
    try {
      return await offlineWork
    } finally {
      if (offlineInFlight === offlineWork) offlineInFlight = null
    }
  }

  return {
    graph,
    lineLookupFor: (theme: Theme) => lineLookups[theme],
    findRoutes: findRoutesOrchestrated,
    routeSource: (index: number) => {
      if (index < 0 || index >= lastRouteSources.length) return undefined
      return lastRouteSources[index]
    },
    dispose: () => offlineRunner.dispose(),
  }
}
